import { useLocation } from 'react-router-dom';
import { ChevronRight, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CommandPalette } from './CommandPalette';
import { useState, useMemo } from 'react';

const ROUTE_LABEL_KEYS: Record<string, string> = {
  '/': 'shell.crumbs.overview',
  '/licenses': 'shell.crumbs.licenses',
  '/licenses/new': 'shell.crumbs.new',
  '/blacklist': 'shell.crumbs.blacklist',
  '/audit': 'shell.crumbs.audit',
  '/requests': 'shell.crumbs.requests',
  '/operators': 'shell.crumbs.operators',
};

function buildBreadcrumbs(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: { labelKey: string; path: string }[] = [];

  let accumulated = '';
  for (let i = 0; i < segments.length; i++) {
    accumulated += '/' + segments[i];
    const exactKey = ROUTE_LABEL_KEYS[accumulated];
    if (exactKey) {
      crumbs.push({ labelKey: exactKey, path: accumulated });
    } else if (i === segments.length - 1) {
      crumbs.push({ labelKey: 'shell.crumbs.details', path: accumulated });
    }
  }

  if (crumbs.length === 0) {
    crumbs.push({ labelKey: 'shell.crumbs.overview', path: '/' });
  }

  return crumbs;
}

function Breadcrumbs() {
  const location = useLocation();
  const { t } = useTranslation();
  const crumbs = useMemo(() => buildBreadcrumbs(location.pathname), [location.pathname]);

  return (
    <nav className="flex items-center gap-1 text-sm text-faint">
      {crumbs.map((crumb, i) => (
        <span key={crumb.path} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          <span className={i === crumbs.length - 1 ? 'font-semibold text-foreground' : ''}>
            {t(crumb.labelKey)}
          </span>
        </span>
      ))}
    </nav>
  );
}

interface TopBarProps {
  onMenuClick: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { t } = useTranslation();
  const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  return (
    <>
      <header className="flex h-16 items-center justify-between border-b bg-background px-4 lg:px-10">
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground lg:hidden"
            aria-label={t('shell.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Breadcrumbs />
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-status-active/25 bg-status-active/10 px-3 py-1.5 text-[13px] font-medium text-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-status-active" aria-hidden="true" />
            {t('shell.envBadge')}
          </span>

          <button
            onClick={() => setPaletteOpen(true)}
            className="hidden items-center gap-1 rounded-[7px] border bg-foreground/5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            aria-label={t('shell.openCommandPalette')}
          >
            {isMac ? '⌘ K' : 'Ctrl K'}
          </button>
        </div>
      </header>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
